import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const NavigationTest: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuType, setMenuType] = useState<'top' | 'side'>('side');
  const [sideMenuOpen, setSideMenuOpen] = useState(true);

  // Handle scroll to shrink navigation
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Navigation items
  const navItems = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/assets', label: 'Assets', icon: '💰' },
    { path: '/liabilities', label: 'Liabilities', icon: '💳' },
    { path: '/cash-flow', label: 'Cash Flow', icon: '📈' },
    { path: '/transactions', label: 'Transactions', icon: '💳' },
    { path: '/cash-flow-2', label: 'CF2 Test', icon: '🧪' },
  ];

  // Top Navigation Component
  const TopNavigation = () => (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}
      style={{ 
        backgroundColor: 'var(--background-card)', 
        borderBottom: '1px solid var(--border-color)',
        backdropFilter: 'blur(10px)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center space-x-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <span className="text-xl">💰</span>
              <span className={`font-bold ${isScrolled ? 'text-lg' : 'text-xl'} transition-all duration-300`}>
                Wealthini
              </span>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'text-white'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                style={{
                  backgroundColor: isActive(item.path) ? 'var(--primary-color)' : 'transparent',
                  color: isActive(item.path) ? 'white' : 'var(--text-secondary)'
                }}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
              >
                {user?.first_name?.[0] || user?.email?.[0] || 'U'}
              </div>
              <span className="hidden sm:block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {user?.first_name || user?.email}
              </span>
            </div>
            <button
              onClick={logout}
              className="px-3 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{ 
                color: 'var(--text-secondary)',
                backgroundColor: 'transparent'
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );

  // Side Navigation Component
  const SideNavigation = () => (
    <>
      {/* Mobile Overlay */}
      {sideMenuOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setSideMenuOpen(false)}
        />
      )}

      {/* Side Menu */}
      <div 
        className={`fixed top-0 left-0 h-full z-50 transform transition-transform duration-300 ease-in-out ${
          sideMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 md:relative md:z-auto`}
        style={{ 
          backgroundColor: 'var(--background-card)',
          borderRight: '1px solid var(--border-color)',
          width: '280px'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <Link 
            to="/" 
            className="flex items-center space-x-2"
            style={{ color: 'var(--text-primary)' }}
          >
            <span className="text-xl">💰</span>
            <span className="font-bold text-lg">Wealthini</span>
          </Link>
          <button
            onClick={() => setSideMenuOpen(false)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            style={{ color: 'var(--text-secondary)' }}
          >
            ✕
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive(item.path)
                  ? 'text-white'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{
                backgroundColor: isActive(item.path) ? 'var(--primary-color)' : 'transparent',
                color: isActive(item.path) ? 'white' : 'var(--text-secondary)'
              }}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center space-x-3 mb-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
              style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
            >
              {user?.first_name?.[0] || user?.email?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                {user?.first_name || 'User'}
              </p>
              <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{ 
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-color)'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setSideMenuOpen(true)}
        className="fixed top-4 left-4 z-50 p-2 rounded-lg md:hidden"
        style={{ 
          backgroundColor: 'var(--background-card)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-color)'
        }}
      >
        ☰
      </button>
    </>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }}>
      {/* Navigation */}
      {menuType === 'top' ? <TopNavigation /> : <SideNavigation />}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${
        menuType === 'top' 
          ? 'pt-20' 
          : 'md:ml-72'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Navigation Test
            </h1>
            <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
              Testing different navigation designs with Tailwind CSS
            </p>
          </div>

          {/* Menu Type Toggle */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm border p-6" style={{ 
              backgroundColor: 'var(--background-card)', 
              borderColor: 'var(--border-color)' 
            }}>
              <h2 className="text-lg font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
                Menu Type
              </h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => setMenuType('top')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    menuType === 'top' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: menuType === 'top' ? 'var(--primary-color)' : 'transparent',
                    color: menuType === 'top' ? 'white' : 'var(--text-secondary)',
                    border: menuType === 'top' ? 'none' : '1px solid var(--border-color)'
                  }}
                >
                  Top Navigation
                </button>
                <button
                  onClick={() => setMenuType('side')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    menuType === 'side' ? 'text-white' : ''
                  }`}
                  style={{
                    backgroundColor: menuType === 'side' ? 'var(--primary-color)' : 'transparent',
                    color: menuType === 'side' ? 'white' : 'var(--text-secondary)',
                    border: menuType === 'side' ? 'none' : '1px solid var(--border-color)'
                  }}
                >
                  Side Navigation
                </button>
              </div>
            </div>
          </div>

          {/* Content Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div 
                key={i}
                className="rounded-lg shadow-sm border p-6 transition-all duration-200 hover:shadow-md"
                style={{ 
                  backgroundColor: 'var(--background-card)', 
                  borderColor: 'var(--border-color)' 
                }}
              >
                <div className="flex items-center mb-4">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                    style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}
                  >
                    <span style={{ color: 'var(--primary-color)' }}>📊</span>
                  </div>
                  <div>
                    <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      Card {i}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Sample content
                    </p>
                  </div>
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  This is a sample card to demonstrate the layout and spacing with the new navigation design.
                </p>
              </div>
            ))}
          </div>

          {/* Scroll Test Content */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
              Scroll Test Content
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Scroll down to see how the navigation behaves. The top navigation will shrink, and the side navigation will remain fixed.
            </p>
            {Array.from({ length: 20 }, (_, i) => (
              <div 
                key={i}
                className="mb-4 p-4 rounded-lg border"
                style={{ 
                  backgroundColor: 'var(--background-card)', 
                  borderColor: 'var(--border-color)' 
                }}
              >
                <h3 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Section {i + 1}
                </h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  This is scroll test content to demonstrate the navigation behavior. Keep scrolling to see the effects.
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationTest;
